import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Header from '@cloudscape-design/components/header';
import Tabs from '@cloudscape-design/components/tabs';
import Table from '@cloudscape-design/components/table';
import Badge from '@cloudscape-design/components/badge';
import Button from '@cloudscape-design/components/button';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import Container from '@cloudscape-design/components/container';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Grid from '@cloudscape-design/components/grid';
import PieChart from '@cloudscape-design/components/pie-chart';
import BarChart from '@cloudscape-design/components/bar-chart';
import CopyToClipboard from '@cloudscape-design/components/copy-to-clipboard';
import Pagination from '@cloudscape-design/components/pagination';
import TextFilter from '@cloudscape-design/components/text-filter';
import CollectionPreferences from '@cloudscape-design/components/collection-preferences';
import Select from '@cloudscape-design/components/select';
import Autosuggest from '@cloudscape-design/components/autosuggest';
import Flashbar from '@cloudscape-design/components/flashbar';
import { useCollection } from '@cloudscape-design/collection-hooks';
import {
  fetchJobDetail,
  updateMapping,
  reassignRows,
  rerunMapping,
  saveAsTemplate,
  JobDetail as JobDetailType,
  FieldMapping,
  TransformRule,
  GroupingSummary,
  GroupResult,
  ValidationResult,
  needsAttention,
} from '../services/api';
import Modal from '@cloudscape-design/components/modal';
import Link from '@cloudscape-design/components/link';
import Spinner from '@cloudscape-design/components/spinner';
import targetSchema from '../data/target-schema.json';
import Alert from '@cloudscape-design/components/alert';
import ApprovalWorkflow from './ApprovalWorkflow';
import AccountTypeSelector from './AccountTypeSelector';
import GroupedApprovalWorkflow from './GroupedApprovalWorkflow';
import GroupingSummaryPanel from './GroupingSummaryPanel';
import { findDuplicateTargets } from '../utils/duplicateDetection';
import TemplateMatchBanner from './TemplateMatchBanner';
import OutputPreviewTab from './OutputPreviewTab';

/* ── Helpers ── */

function confidenceBadgeColor(score: number): 'blue' | 'grey' | 'red' | 'green' {
  if (score >= 90) return 'green';
  if (score >= 70) return 'blue';
  if (score >= 50) return 'grey';
  return 'red';
}

function isUnmapped(mapping: FieldMapping): boolean {
  return mapping.classification === 'not-applicable';
}

function isEmptyField(mapping: FieldMapping): boolean {
  const val = mapping.sampleValue?.trim();
  return !val || val === '' || val.toLowerCase() === 'null' || val.toLowerCase() === 'nan';
}

function getNestedValue(obj: any, path: string): string {
  if (!obj || !path || path === 'unmapped') return '';
  const parts = path.replace(/\[\]/g, '[0]').split('.');
  let current: any = obj;
  for (const part of parts) {
    const match = part.match(/^(.+)\[(\d+)\]$/);
    if (match) {
      current = current?.[match[1]]?.[parseInt(match[2])];
    } else {
      current = current?.[part];
    }
    if (current === undefined || current === null) return '';
  }
  return String(current);
}

/* ── Enriched mapping type ── */

interface EnrichedMapping extends FieldMapping {
  _sourceValue: string;
  _mappedValue: string;
}

/* ── Inline Target Field Editor ── */

function TargetFieldCell({
  mapping,
  allTargetFields,
  currentMappings,
  isChanged,
  onSave,
}: {
  mapping: EnrichedMapping;
  allTargetFields: string[];
  currentMappings: FieldMapping[];
  isChanged: boolean;
  onSave: (sourceField: string, newTarget: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(mapping.targetField);
  const [saving, setSaving] = useState(false);

  const options = allTargetFields
    .filter(f => f.toLowerCase().includes(value.toLowerCase()) || value === '')
    .map(f => ({ value: f }));

  // Check if the chosen target is already used by another source field (skip "unmapped")
  const isDuplicate = value &&
    value !== mapping.targetField &&
    value.toLowerCase() !== 'unmapped' &&
    !value.toLowerCase().startsWith('application.unmapped.') &&
    currentMappings.some(m => m.sourceField !== mapping.sourceField && m.targetField === value);

  const handleSave = async () => {
    if (value === mapping.targetField) {
      setEditing(false);
      return;
    }
    setSaving(true);
    await onSave(mapping.sourceField, value);
    setSaving(false);
    setEditing(false);
  };

  if (editing) {
    return (
      <SpaceBetween size="xxs">
        <SpaceBetween direction="horizontal" size="xxs">
          <div style={{ minWidth: 220 }}>
            <Autosuggest
              value={value}
              onChange={({ detail }) => setValue(detail.value)}
              options={options}
              enteredTextLabel={v => `Use: "${v}"`}
              placeholder="Select target field"
              empty="No matching fields"
              expandToViewport
              autoFocus
            />
          </div>
          <Button variant="primary" loading={saving} onClick={handleSave} iconName="check" ariaLabel="Save" disabled={!!isDuplicate} />
          <Button variant="link" onClick={() => { setEditing(false); setValue(mapping.targetField); }}>Cancel</Button>
        </SpaceBetween>
        {isDuplicate && (
          <Box variant="small" color="text-status-error">
            ⚠ Already mapped by another source field
          </Box>
        )}
      </SpaceBetween>
    );
  }

  // Confidence + status badges
  const empty = isEmptyField(mapping);
  const unmapped = isUnmapped(mapping);

  let confidenceBadge: React.ReactNode;
  let statusIndicator: React.ReactNode;

  if (isChanged || mapping.isModified) {
    confidenceBadge = <Badge color="blue">CUSTOM</Badge>;
    statusIndicator = <StatusIndicator type="info">Manual override</StatusIndicator>;
  } else if (empty || unmapped) {
    confidenceBadge = <Badge color="grey">N/A</Badge>;
    statusIndicator = empty
      ? <StatusIndicator type="info">Empty</StatusIndicator>
      : <StatusIndicator type="info">No target</StatusIndicator>;
  } else if (needsAttention(mapping)) {
    confidenceBadge = <Badge color={confidenceBadgeColor(mapping.confidenceScore)}>{mapping.confidenceScore}%</Badge>;
    statusIndicator = <StatusIndicator type="warning">Review</StatusIndicator>;
  } else {
    confidenceBadge = <Badge color={confidenceBadgeColor(mapping.confidenceScore)}>{mapping.confidenceScore}%</Badge>;
    statusIndicator = <StatusIndicator type="success">OK</StatusIndicator>;
  }

  return (
    <SpaceBetween direction="horizontal" size="xxs">
      <SpaceBetween size="xxs">
        <Box fontWeight="bold">{mapping.targetField || '—'}</Box>
        <SpaceBetween direction="horizontal" size="xs">
          {confidenceBadge}
          {statusIndicator}
        </SpaceBetween>
      </SpaceBetween>
      <Button
        variant="inline-icon"
        iconName="edit"
        ariaLabel={`Edit target for ${mapping.sourceField}`}
        onClick={() => setEditing(true)}
      />
    </SpaceBetween>
  );
}

/* ── Inline Transform Editor ── */

function describeTransform(rule: TransformRule | null | undefined): string {
  if (!rule) return '';
  switch (rule.type) {
    case 'prefix': return `Prepend "${rule.value || ''}"`;
    case 'suffix': return `Append "${rule.value || ''}"`;
    case 'regex': return `Regex: s/${rule.pattern || ''}/${rule.replacement || ''}/`;
    case 'upper': return 'Uppercase';
    case 'lower': return 'Lowercase';
    case 'strip': return 'Trim whitespace';
    case 'country': return 'Country conversion';
    case 'date': case 'dateFormat': return 'Date conversion';
    case 'phone': return 'Phone conversion';
    case 'boolean': return 'Boolean conversion';
    case 'state': return 'State conversion';
    case 'lookup': return `Lookup (${Object.keys(rule.map || {}).length} entries)`;
    default: return rule.type;
  }
}

function TransformCell({
  mapping,
  onAskAgent,
}: {
  mapping: EnrichedMapping;
  onAskAgent: (prompt: string) => void;
}) {
  if (mapping.transformRule) {
    return (
      <SpaceBetween direction="horizontal" size="xxs">
        <Badge color="blue">{describeTransform(mapping.transformRule)}</Badge>
        <Button
          variant="inline-icon"
          iconName="gen-ai"
          ariaLabel={`Modify transform for ${mapping.sourceField} via agent`}
          onClick={() => onAskAgent(`I want to change the transform rule on "${mapping.sourceField}" (currently: ${describeTransform(mapping.transformRule)}). What do you suggest?`)}
        />
      </SpaceBetween>
    );
  }

  return (
    <Button
      variant="inline-link"
      iconName="gen-ai"
      onClick={() => onAskAgent(`Add a transform/translation rule for the field "${mapping.sourceField}" which maps to "${mapping.targetField}". The sample value is "${mapping._sourceValue || mapping.sampleValue}". The details are:`)}
    >
      Ask agent
    </Button>
  );
}

/* ── Mappings Tab ── */

interface MappingsTabProps {
  mappings: FieldMapping[];
  csvRows: Record<string, string>[];
  outputRows: object[];
  allTargetFields: string[];
  onTargetFieldChange: (sourceField: string, newTarget: string) => Promise<void>;
  onAskAgent: (prompt: string) => void;
  changedFields: Set<string>;
  onFieldSelect?: (data: import('../App').FieldPreviewData) => void;
  rowGroupings?: Record<string, number[]>;
  jobId?: string;
  reassigning?: boolean;
  onReassign?: (rowIndex: number, targetAccountType: string) => Promise<void>;
  groupResults?: GroupResult[];
  onRecordGroupChange?: (accountType: string) => void;
  validationWarnings?: { field: string; message: string }[];
}

function MappingsTab({
  mappings,
  csvRows,
  outputRows,
  allTargetFields,
  onTargetFieldChange,
  onAskAgent,
  changedFields,
  onFieldSelect,
  rowGroupings,
  jobId,
  reassigning,
  onReassign,
  groupResults,
  onRecordGroupChange,
  validationWarnings,
}: MappingsTabProps) {
  const [pageSize, setPageSize] = useState(25);
  const [selectedRow, setSelectedRow] = useState(0);
  const [selectedItems, setSelectedItems] = useState<EnrichedMapping[]>([]);
  const [viewFilter, setViewFilter] = useState<string>('all');
  const [groupFilter, setGroupFilter] = useState<string>('all');
  const totalRows = csvRows.length;
  const attentionCount = mappings.filter(m => needsAttention(m) && !isEmptyField(m)).length;

  // Track the actual CSV row index to restore after reassignment
  const pendingRowRestoreRef = useRef<number | null>(null);
  // Track whether the group filter change was triggered by reassignment logic
  const reassignFilterSwitchRef = useRef(false);

  // Build row-to-account-type lookup from rowGroupings
  const rowToAccountType = React.useMemo(() => {
    if (!rowGroupings) return null;
    return buildRowToAccountType(rowGroupings);
  }, [rowGroupings]);

  // Build group filter options
  const groupFilterOptions = React.useMemo(() => {
    const opts = [{ value: 'all', label: 'All records' }];
    if (rowGroupings) {
      for (const acctType of Object.keys(rowGroupings)) {
        const count = rowGroupings[acctType].length;
        opts.push({ value: acctType, label: `${acctType} (${count} rows)` });
      }
      // Count unclassified
      const classifiedIndices = new Set(Object.values(rowGroupings).flat());
      const unclassifiedCount = totalRows - classifiedIndices.size;
      if (unclassifiedCount > 0) {
        opts.push({ value: '_unclassified', label: `Unclassified (${unclassifiedCount} rows)` });
      }
    }
    return opts;
  }, [rowGroupings, totalRows]);

  // Filter row indices based on group filter
  const filteredRowIndices = React.useMemo(() => {
    if (!rowGroupings || groupFilter === 'all') {
      return Array.from({ length: totalRows }, (_, i) => i);
    }
    if (groupFilter === '_unclassified') {
      const classifiedIndices = new Set(Object.values(rowGroupings).flat());
      return Array.from({ length: totalRows }, (_, i) => i).filter(i => !classifiedIndices.has(i));
    }
    return (rowGroupings[groupFilter] ?? []).filter(i => i < totalRows).sort((a, b) => a - b);
  }, [rowGroupings, groupFilter, totalRows]);

  // Reset selected row when group filter changes (unless triggered by reassignment restore)
  React.useEffect(() => {
    if (reassignFilterSwitchRef.current) {
      // Don't reset — the restore effect below will handle positioning
      reassignFilterSwitchRef.current = false;
      return;
    }
    setSelectedRow(0);
  }, [groupFilter]);

  // After filteredRowIndices recompute, restore selection to the previously viewed CSV row
  React.useEffect(() => {
    const targetRow = pendingRowRestoreRef.current;
    if (targetRow === null) return;

    const idx = filteredRowIndices.indexOf(targetRow);
    if (idx >= 0) {
      // Row is still in the current filter — select it
      setSelectedRow(idx);
      pendingRowRestoreRef.current = null;
    } else if (groupFilter !== 'all') {
      // Row moved out of the active group filter — switch to "all" to find it
      reassignFilterSwitchRef.current = true;
      setGroupFilter('all');
      // pendingRowRestoreRef stays set so the next recompute of filteredRowIndices will find it
    } else {
      // "all" filter but row not found (shouldn't happen) — clear and stay at 0
      pendingRowRestoreRef.current = null;
      setSelectedRow(0);
    }
  }, [filteredRowIndices, groupFilter]);

  // The actual CSV row index for the current selection
  const actualRowIndex = filteredRowIndices[selectedRow] ?? 0;

  // Notify parent when the current record's account type changes
  const currentRecordGroup = rowToAccountType?.get(actualRowIndex);
  React.useEffect(() => {
    if (currentRecordGroup && onRecordGroupChange) {
      onRecordGroupChange(currentRecordGroup);
    }
  }, [currentRecordGroup, onRecordGroupChange]);

  // Resolve target fields for the currently displayed row based on its account type
  const targetFieldsForRow = React.useMemo(() => {
    const acctType = rowToAccountType?.get(actualRowIndex);
    if (!acctType) return allTargetFields; // fallback for unclassified
    return (targetSchema as Record<string, string[]>)[acctType] ?? allTargetFields;
  }, [actualRowIndex, rowToAccountType, allTargetFields]);

  // Scope mappings (including transform rules) to the current row's account type
  const mappingsForRow = React.useMemo(() => {
    const acctType = rowToAccountType?.get(actualRowIndex);
    if (!acctType || !groupResults) {
      // Unclassified row: strip transform rules
      return mappings.map(m => ({ ...m, transformRule: null }));
    }
    const groupResult = groupResults.find(g => g.accountType === acctType);
    return groupResult?.mappings ?? mappings;
  }, [actualRowIndex, rowToAccountType, groupResults, mappings]);

  const handleSelectionChange = useCallback((selected: EnrichedMapping[]) => {
    setSelectedItems(selected);
    const mapping = selected[0];
    if (!mapping || !onFieldSelect) return;
    // Only show records in the current group (or all if no groupings)
    const currentGroup = rowToAccountType?.get(actualRowIndex);
    const groupIndices = currentGroup && rowGroupings
      ? new Set(rowGroupings[currentGroup] ?? [])
      : null;
    const records = csvRows
      .map((row, idx) => ({
        row: idx + 1,
        sourceValue: row[mapping.sourceField] || '',
        mappedValue: outputRows.length > idx ? getNestedValue(outputRows[idx], mapping.targetField) : '',
        _idx: idx,
      }))
      .filter(r => !groupIndices || groupIndices.has(r._idx));
    onFieldSelect({ sourceField: mapping.sourceField, targetField: mapping.targetField, records });
  }, [csvRows, outputRows, onFieldSelect, rowToAccountType, actualRowIndex, rowGroupings]);

  // Deduplicate mappingsForRow by sourceField to avoid React key collisions
  // (group results can contain the same sourceField mapped in multiple contexts)
  const dedupedMappings = React.useMemo(() => {
    const seen = new Set<string>();
    return mappingsForRow.filter(m => {
      if (seen.has(m.sourceField)) return false;
      seen.add(m.sourceField);
      return true;
    });
  }, [mappingsForRow]);

  const enrichedMappings: EnrichedMapping[] = dedupedMappings.map(m => {
    const sourceValue = totalRows > 0 ? (csvRows[actualRowIndex]?.[m.sourceField] || '') : m.sampleValue;
    const mappedValue = outputRows.length > 0 ? getNestedValue(outputRows[actualRowIndex], m.targetField) : m.mappedValue;
    return { ...m, _sourceValue: sourceValue, _mappedValue: mappedValue };
  });

  // Apply view filter
  let filteredMappings = enrichedMappings;
  if (viewFilter === 'unmapped') {
    filteredMappings = enrichedMappings.filter(m => isUnmapped(m) || !m.targetField);
  } else if (viewFilter === 'custom') {
    filteredMappings = enrichedMappings.filter(m => changedFields.has(m.sourceField) || m.isModified);
  } else if (viewFilter === 'review') {
    filteredMappings = enrichedMappings.filter(m => needsAttention(m) && !isEmptyField(m));
  } else if (viewFilter === 'confidence') {
    filteredMappings = [...enrichedMappings].sort((a, b) => a.confidenceScore - b.confidenceScore);
  }

  const viewFilterOptions = [
    { value: 'all', label: 'All mappings' },
    { value: 'confidence', label: 'Asc confidence' },
    { value: 'unmapped', label: 'Unmapped' },
    { value: 'custom', label: 'Custom' },
    { value: 'review', label: 'Pending review' },
  ];

  const colDefs = [
    {
      id: 'sourceField',
      header: 'Source',
      width: 260,
      cell: (m: EnrichedMapping) => (
        <SpaceBetween size="xxs">
          <span style={{ fontWeight: changedFields.has(m.sourceField) ? 600 : 'normal' }}>
            {m.sourceField}
            {changedFields.has(m.sourceField) && (
              <Box display="inline-block" margin={{ left: 'xxs' }}>
                <Badge color="blue">updated</Badge>
              </Box>
            )}
          </span>
          {m._sourceValue && (
            <Box color="text-body-secondary" variant="small">
              <span style={{ display: 'block', maxWidth: 230, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {m._sourceValue}
              </span>
            </Box>
          )}
        </SpaceBetween>
      ),
      sortingField: 'sourceField',
    },
    {
      id: 'targetField',
      header: 'Target',
      width: 280,
      cell: (m: EnrichedMapping) => (
        <TargetFieldCell
          mapping={m}
          allTargetFields={targetFieldsForRow}
          currentMappings={mappings}
          isChanged={changedFields.has(m.sourceField)}
          onSave={onTargetFieldChange}
        />
      ),
      sortingField: 'targetField',
    },
    {
      id: 'mappedValue',
      header: 'Mapped Value',
      width: 180,
      cell: (m: EnrichedMapping) => (
        <Box variant="code">
          <span style={{ display: 'block', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {m._mappedValue || '—'}
          </span>
        </Box>
      ),
    },
    {
      id: 'transform',
      header: 'Transform',
      width: 120,
      cell: (m: EnrichedMapping) => (
        <TransformCell mapping={m} onAskAgent={onAskAgent} />
      ),
    },
  ];

  const { items, filteredItemsCount, collectionProps, filterProps, paginationProps } = useCollection(
    filteredMappings,
    {
      filtering: {
        filteringFunction: (item: EnrichedMapping, filterText: string) => {
          const t = filterText.toLowerCase();
          return (
            item.sourceField.toLowerCase().includes(t) ||
            item.targetField.toLowerCase().includes(t) ||
            item.classification.toLowerCase().includes(t) ||
            (item._sourceValue || '').toLowerCase().includes(t) ||
            (item._mappedValue || '').toLowerCase().includes(t)
          );
        },
      },
      pagination: { pageSize },
      sorting: { defaultState: { sortingColumn: colDefs[0], isDescending: false } },
    },
  );

  const rowOptions = filteredRowIndices.map((rowIdx, i) => {
    const acctType = rowToAccountType?.get(rowIdx);
    const label = acctType
      ? `Record ${rowIdx + 1} (${acctType})`
      : rowToAccountType
        ? `Record ${rowIdx + 1} (Unclassified)`
        : `Record ${rowIdx + 1}`;
    return { label, value: String(i) };
  });

  const currentAccountType = rowToAccountType?.get(actualRowIndex);

  const accountTypeOptions = [
    { value: 'individual', label: 'Individual' },
    { value: 'joint', label: 'Joint' },
    { value: 'company', label: 'Company' },
    { value: 'individualtrust', label: 'Individual Trust' },
    { value: 'corporatetrust', label: 'Corporate Trust' },
  ];

  const handleAccountTypeChange = useCallback(
    async (newType: string) => {
      if (!onReassign) return;
      if (newType === currentAccountType) return;
      // Save the actual CSV row index so we can restore selection after reload
      pendingRowRestoreRef.current = actualRowIndex;
      await onReassign(actualRowIndex, newType);
    },
    [onReassign, currentAccountType, actualRowIndex],
  );

  return (
    <SpaceBetween size="s">
      {/* Missing required fields warning */}
      {validationWarnings && validationWarnings.length > 0 && (
        <Alert type="warning" header={`${validationWarnings.length} required SwiftID field${validationWarnings.length > 1 ? 's' : ''} unmapped`}>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {validationWarnings.map((w, i) => (
              <li key={i}>{w.field}</li>
            ))}
          </ul>
        </Alert>
      )}
      {filteredRowIndices.length > 0 && (
        <Container>
          <SpaceBetween direction="horizontal" size="xs">
            {rowGroupings && (
              <div style={{ minWidth: 180 }}>
                <Select
                  selectedOption={groupFilterOptions.find(o => o.value === groupFilter) || groupFilterOptions[0]}
                  onChange={({ detail }) => setGroupFilter(detail.selectedOption.value || 'all')}
                  options={groupFilterOptions}
                />
              </div>
            )}
            <Button
              disabled={selectedRow === 0}
              onClick={() => setSelectedRow(selectedRow - 1)}
              iconName="angle-left"
            >
              Prev
            </Button>
            <Box variant="p" padding={{ top: 'xs' }}>
              Record {actualRowIndex + 1} of {totalRows}
              {filteredRowIndices.length !== totalRows && ` (${filteredRowIndices.length} in filter)`}
            </Box>
            {currentAccountType !== undefined && (
              <Box padding={{ top: 'xxs' }}>
                <Badge color={getAccountTypeBadgeColor(currentAccountType ?? '')}>{currentAccountType ?? 'Unclassified'}</Badge>
              </Box>
            )}
            {currentAccountType === undefined && rowToAccountType && (
              <Box padding={{ top: 'xxs' }}>
                <Badge color="red">Unclassified</Badge>
              </Box>
            )}
            {rowToAccountType && (
              <div style={{ minWidth: 160, display: 'flex', alignItems: 'center', gap: 4 }}>
                {reassigning && <Spinner size="normal" />}
                <Select
                  selectedOption={
                    currentAccountType
                      ? accountTypeOptions.find(o => o.value === currentAccountType) || null
                      : null
                  }
                  onChange={({ detail }) => {
                    const val = detail.selectedOption.value;
                    if (val) handleAccountTypeChange(val);
                  }}
                  options={accountTypeOptions}
                  placeholder="Assign type…"
                  disabled={reassigning}
                  data-testid="account-type-reassign-select"
                />
              </div>
            )}
            <Select
              selectedOption={rowOptions[selectedRow] || null}
              onChange={({ detail }) => setSelectedRow(parseInt(detail.selectedOption.value || '0'))}
              options={rowOptions}
            />
            <Button
              disabled={selectedRow >= filteredRowIndices.length - 1}
              onClick={() => setSelectedRow(selectedRow + 1)}
              iconName="angle-right"
            >
              Next
            </Button>
          </SpaceBetween>
        </Container>
      )}
      <Table
        {...collectionProps}
        items={items}
        columnDefinitions={colDefs}
        selectionType="single"
        selectedItems={selectedItems}
        onSelectionChange={({ detail }) => handleSelectionChange(detail.selectedItems as EnrichedMapping[])}
        trackBy="sourceField"
        header={
          <Header counter={`(${mappings.length} mappings, ${attentionCount} need attention)`}>
            Field Mappings
          </Header>
        }
        filter={
          <SpaceBetween direction="horizontal" size="xs">
            <div style={{ minWidth: 170 }}>
              <Select
                selectedOption={viewFilterOptions.find(o => o.value === viewFilter) || viewFilterOptions[0]}
                onChange={({ detail }) => setViewFilter(detail.selectedOption.value || 'all')}
                options={viewFilterOptions}
              />
            </div>
            <TextFilter
              {...filterProps}
              filteringPlaceholder="Search mappings"
              countText={`${filteredItemsCount ?? 0} matches`}
            />
          </SpaceBetween>
        }
        pagination={<Pagination {...paginationProps} />}
        preferences={
          <CollectionPreferences
            title="Preferences"
            confirmLabel="Confirm"
            cancelLabel="Cancel"
            preferences={{ pageSize }}
            pageSizePreference={{
              title: 'Page size',
              options: [
                { value: 10, label: '10' },
                { value: 25, label: '25' },
                { value: 50, label: '50' },
                { value: 100, label: '100' },
              ],
            }}
            onConfirm={({ detail: d }) => setPageSize(d.pageSize ?? 25)}
          />
        }
        empty="No mappings"
      />
    </SpaceBetween>
  );
}

/* ── Input Preview Tab ── */

const ACCOUNT_TYPE_COLORS: Record<string, 'blue' | 'green' | 'grey' | 'red'> = {
  individual: 'blue',
  joint: 'green',
  company: 'grey',
  individualtrust: 'blue',
  corporatetrust: 'green',
};

function getAccountTypeBadgeColor(accountType: string): 'blue' | 'green' | 'grey' | 'red' {
  return ACCOUNT_TYPE_COLORS[accountType.toLowerCase()] ?? 'grey';
}

/** Build a simple fingerprint of a group's mappings so we can detect when they change. */
function computeMappingFingerprint(mappings: FieldMapping[]): string {
  return mappings
    .map(m => `${m.sourceField}:${m.targetField}:${m.confidenceScore}`)
    .sort()
    .join('|');
}

export function buildRowToAccountType(rowGroupings: Record<string, number[]>): Map<number, string> {
  const lookup = new Map<number, string>();
  for (const [accountType, indices] of Object.entries(rowGroupings)) {
    for (const idx of indices) {
      lookup.set(idx, accountType);
    }
  }
  return lookup;
}

function InputPreviewTab({ csvLines, rowGroupings }: { csvLines: string[]; rowGroupings?: Record<string, number[]> }) {
  const [pageSize, setPageSize] = useState(10);
  const csvHeaders = csvLines.length > 0 ? csvLines[0].split(',').map(h => h.trim()) : [];
  const rowToAccountType = rowGroupings ? buildRowToAccountType(rowGroupings) : null;
  const csvRows = csvLines.slice(1).map((line, idx) => {
    const values = line.split(',');
    const row: Record<string, string> = { _rowIndex: String(idx + 1) };
    csvHeaders.forEach((header, i) => {
      row[header] = values[i]?.trim() || '';
    });
    if (rowToAccountType) {
      row._accountType = rowToAccountType.get(idx) ?? 'Unclassified';
    }
    return row;
  });

  const accountTypeColumn = rowToAccountType
    ? [{
        id: '_accountType',
        header: 'Account Type',
        cell: (row: Record<string, string>) => {
          const acctType = row._accountType;
          if (acctType === 'Unclassified') {
            return <Badge color="red">Unclassified</Badge>;
          }
          return <Badge color={getAccountTypeBadgeColor(acctType)}>{acctType}</Badge>;
        },
        width: 150,
      }]
    : [];

  const columnDefs = [
    { id: '_row', header: 'Row', cell: (row: Record<string, string>) => row._rowIndex, width: 70 },
    ...accountTypeColumn,
    ...csvHeaders.map(h => ({
      id: h,
      header: h,
      cell: (row: Record<string, string>) => row[h] || '',
    })),
  ];
  const { items, filteredItemsCount, collectionProps, filterProps, paginationProps } = useCollection(
    csvRows,
    {
      filtering: {
        filteringFunction: (item: Record<string, string>, filterText: string) => {
          const t = filterText.toLowerCase();
          return Object.values(item).some(v => v.toLowerCase().includes(t));
        },
      },
      pagination: { pageSize },
      sorting: {},
    },
  );
  if (csvHeaders.length === 0) return <Box>No input file preview available</Box>;
  return (
    <Table
      {...collectionProps}
      items={items}
      columnDefinitions={columnDefs}
      header={<Header counter={`(${csvRows.length} records)`}>Source Data</Header>}
      filter={
        <TextFilter
          {...filterProps}
          filteringPlaceholder="Search records"
          countText={`${filteredItemsCount ?? 0} matches`}
        />
      }
      pagination={<Pagination {...paginationProps} />}
      preferences={
        <CollectionPreferences
          title="Preferences"
          confirmLabel="Confirm"
          cancelLabel="Cancel"
          preferences={{ pageSize }}
          pageSizePreference={{
            title: 'Page size',
            options: [
              { value: 10, label: '10' },
              { value: 25, label: '25' },
              { value: 50, label: '50' },
            ],
          }}
          onConfirm={({ detail: d }) => setPageSize(d.pageSize ?? 10)}
        />
      }
      stickyColumns={{ first: 1 }}
      empty="No data"
    />
  );
}

/* ── Summary Tab (Dashboard) ── */

function SummaryTab({ summary, mappings, groupingSummary, groupResults, groupValidationResults }: {
  summary: JobDetailType['summary'];
  mappings: FieldMapping[];
  groupingSummary?: GroupingSummary;
  groupResults?: GroupResult[];
  groupValidationResults?: ValidationResult;
}) {
  const totalRows = groupingSummary?.totalRows ?? summary.metrics?.totalRecords ?? 0;
  const groups = groupingSummary?.groups ?? [];
  const hasGroups = groups.length > 0 && groupResults && groupResults.length > 0;

  // Build per-group stats
  const groupStats = React.useMemo(() => {
    if (!hasGroups || !groupResults) return [];
    return groupResults.map((gr) => {
      const detail = groups.find((g) => g.accountType === gr.accountType);
      const active = gr.mappings.filter((m) => m.classification !== 'not-applicable');
      const unmapped = gr.mappings.filter((m) => m.classification === 'not-applicable' || !m.targetField).length;
      const needsAttention = active.filter((m) => m.confidenceScore < 70).length;
      return {
        accountType: gr.accountType,
        rowCount: detail?.rowCount ?? 0,
        avgConfidence: detail?.avgConfidence ?? 0,
        mappedFields: active.length,
        totalSourceColumns: gr.mappings.length,
        unmapped,
        needsAttention,
        mappings: gr.mappings,
      };
    });
  }, [hasGroups, groupResults, groups]);

  // Classification data per account type for bar chart
  const classificationBarData = React.useMemo(() => {
    if (!hasGroups) return null;
    const categories: { key: string; label: string; color: string }[] = [
      { key: 'auto-mapped', label: 'auto mapped', color: '#0972d3' },
      { key: 'review-recommended', label: 'review recommended', color: '#2ea043' },
      { key: 'warning', label: 'warning', color: '#d91515' },
      { key: 'requires-human-decision', label: 'requires human decision', color: '#8b5cf6' },
    ];
    const series = categories.map((cat) => ({
      title: cat.label,
      type: 'bar' as const,
      color: cat.color,
      data: groupStats.map((g) => ({
        x: g.accountType,
        y: g.mappings.filter((m) => m.classification === cat.key).length,
      })),
    }));
    return { series, xDomain: groupStats.map((g) => g.accountType) };
  }, [hasGroups, groupStats]);

  // Confidence bar chart: one bar per account type showing avg confidence
  const confidenceBarData = React.useMemo(() => {
    if (!hasGroups) return null;
    return {
      series: [{ title: 'Avg Confidence', type: 'bar' as const, data: groupStats.map((g) => ({ x: g.accountType, y: Math.round(g.avgConfidence) })) }],
      xDomain: groupStats.map((g) => g.accountType),
    };
  }, [hasGroups, groupStats]);

  // Fallback for single-group jobs
  const activeMappings = mappings.filter((m) => m.classification !== 'not-applicable');
  const unmappedCount = mappings.filter((m) => m.classification === 'not-applicable' || !m.targetField).length;
  const needsAttentionCount = activeMappings.filter((m) => m.confidenceScore < 70).length;

  return (
    <SpaceBetween size="l">
      {/* Title row with total rows */}
      <Header variant="h2" description={`${totalRows} total rows across ${groups.length || 1} account type${groups.length !== 1 ? 's' : ''}`}>
        Job Summary
      </Header>

      {/* Validation Results (per-group when available) */}
      {groupValidationResults && (groupValidationResults.errors.length > 0 || groupValidationResults.warnings.length > 0) && (
        <Container header={<Header variant="h2">Validation Results</Header>}>
          <SpaceBetween size="s">
            {groupValidationResults.errors.length > 0 && (
              <Alert type="error">
                {groupValidationResults.errors.map((e, i) => (
                  <div key={i}>{typeof e === 'string' ? e : `${e.field}: ${e.message}`}</div>
                ))}
              </Alert>
            )}
            {groupValidationResults.warnings.length > 0 && (
              <Alert type="warning">
                {groupValidationResults.warnings.map((w, i) => (
                  <div key={i}>{typeof w === 'string' ? w : `${w.field}: ${w.message}`}</div>
                ))}
              </Alert>
            )}
          </SpaceBetween>
        </Container>
      )}

      {/* Per-account-type overview cards — always 5 across */}
      {hasGroups ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
          {['company', 'individual', 'joint', 'individualtrust', 'corporatetrust'].map((acctType) => {
            const g = groupStats.find((s) => s.accountType === acctType);
            const isActive = !!g;
            return (
              <Container
                key={acctType}
                header={
                  <Header variant="h3">
                    <span style={{ fontSize: '13px', opacity: isActive ? 1 : 0.4 }}>
                      {acctType} {isActive ? `(${g!.rowCount})` : ''}
                    </span>
                  </Header>
                }
              >
                {isActive ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 8px', fontSize: '13px' }}>
                    <div>
                      <div style={{ color: '#5f6b7a', fontSize: '11px' }}>Mapped</div>
                      <div style={{ fontWeight: 700 }}>{g!.mappedFields}<span style={{ color: '#5f6b7a', fontWeight: 400 }}>/{g!.totalSourceColumns}</span></div>
                    </div>
                    <div>
                      <div style={{ color: '#5f6b7a', fontSize: '11px' }}>Unmapped</div>
                      <div style={{ fontWeight: 700, color: g!.unmapped > 0 ? '#5f6b7a' : undefined }}>{g!.unmapped}</div>
                    </div>
                    <div>
                      <div style={{ color: '#5f6b7a', fontSize: '11px' }}>Attention</div>
                      <div style={{ fontWeight: 700, color: g!.needsAttention > 0 ? '#d91515' : undefined }}>{g!.needsAttention}</div>
                    </div>
                    <div>
                      <div style={{ color: '#5f6b7a', fontSize: '11px' }}>Confidence</div>
                      <div style={{ fontWeight: 700 }}>{Math.round(g!.avgConfidence)}%</div>
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '12px 0', color: '#5f6b7a', fontSize: '12px', opacity: 0.5 }}>
                    No records
                  </div>
                )}
              </Container>
            );
          })}
        </div>
      ) : (
        /* Single-group fallback */
        <Container header={<Header variant="h2">Overview</Header>}>
          <ColumnLayout columns={4} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Records</Box>
              <Box variant="awsui-value-large">{totalRows}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Mapped Fields</Box>
              <Box variant="awsui-value-large">{activeMappings.length}</Box>
              <Box variant="small" color="text-body-secondary">of {summary.metrics?.totalSourceColumns ?? '—'} source columns</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Unmapped</Box>
              <Box variant="awsui-value-large" color={unmappedCount > 0 ? 'text-status-warning' : undefined}>{unmappedCount}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Needs Attention</Box>
              <Box variant="awsui-value-large" color={needsAttentionCount > 0 ? 'text-status-error' : undefined}>{needsAttentionCount}</Box>
            </div>
          </ColumnLayout>
        </Container>
      )}

      {/* Charts: Classification + Confidence per account type */}
      {hasGroups && classificationBarData && confidenceBarData ? (
        <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
          <Container header={<Header variant="h2">Mapping Confidence by Account Type</Header>}>
            <BarChart
              series={classificationBarData.series}
              xDomain={classificationBarData.xDomain}
              yDomain={[0, Math.max(...classificationBarData.series.flatMap((s) => s.data.map((d) => d.y)), 1)]}
              xTitle="Account Type"
              yTitle="Fields"
              hideFilter
              height={200}
              empty="No data"
              noMatch="No matching data"
            />
          </Container>
          <Container header={<Header variant="h2">Account Classification by Type</Header>}>
            <BarChart
              series={confidenceBarData.series}
              xDomain={confidenceBarData.xDomain}
              yDomain={[0, 100]}
              xTitle="Account Type"
              yTitle="Confidence %"
              hideFilter
              height={200}
              empty="No data"
              noMatch="No matching data"
            />
          </Container>
        </Grid>
      ) : (
        /* Single-group fallback charts */
        <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
          <Container header={<Header variant="h2">Classification Breakdown</Header>}>
            <PieChart
              data={Object.entries(
                activeMappings.reduce((acc, m) => { acc[m.classification.replace(/-/g, ' ')] = (acc[m.classification.replace(/-/g, ' ')] || 0) + 1; return acc; }, {} as Record<string, number>),
              ).filter(([, v]) => v > 0).map(([title, value]) => ({ title, value }))}
              size="medium"
              variant="donut"
              innerMetricDescription="mapped"
              innerMetricValue={String(activeMappings.length)}
              hideFilter
              empty="No data"
              noMatch="No matching data"
            />
          </Container>
          <Container header={<Header variant="h2">Confidence Distribution</Header>}>
            <BarChart
              series={[{ title: 'Fields', type: 'bar', data: (() => {
                const buckets = { 'High (90-100)': 0, 'Good (70-89)': 0, 'Low (50-69)': 0, 'Critical (<50)': 0 };
                activeMappings.forEach((m) => {
                  if (m.confidenceScore >= 90) buckets['High (90-100)']++;
                  else if (m.confidenceScore >= 70) buckets['Good (70-89)']++;
                  else if (m.confidenceScore >= 50) buckets['Low (50-69)']++;
                  else buckets['Critical (<50)']++;
                });
                return Object.entries(buckets).map(([x, y]) => ({ x, y }));
              })() }]}
              xDomain={['High (90-100)', 'Good (70-89)', 'Low (50-69)', 'Critical (<50)']}
              yDomain={[0, Math.max(activeMappings.length, 1)]}
              xTitle="Confidence Range"
              yTitle="Count"
              hideFilter
              height={200}
              empty="No data"
              noMatch="No matching data"
            />
          </Container>
        </Grid>
      )}

      {/* Needs Attention — fields requiring review, grouped by account type */}
      {(() => {
        // Build attention items from per-group data (or flat mappings for single-group)
        type AttentionItem = { accountType: string; sourceField: string; targetField: string; confidence: number; classification: string };
        const attentionItems: AttentionItem[] = [];

        if (hasGroups && groupResults) {
          for (const gr of groupResults) {
            for (const m of gr.mappings) {
              if (m.classification !== 'not-applicable' && m.confidenceScore < 70) {
                attentionItems.push({
                  accountType: gr.accountType,
                  sourceField: m.sourceField,
                  targetField: m.targetField,
                  confidence: m.confidenceScore,
                  classification: m.classification,
                });
              }
            }
          }
        } else {
          for (const m of mappings) {
            if (m.classification !== 'not-applicable' && m.confidenceScore < 70) {
              attentionItems.push({
                accountType: summary.accountType || 'unknown',
                sourceField: m.sourceField,
                targetField: m.targetField,
                confidence: m.confidenceScore,
                classification: m.classification,
              });
            }
          }
        }

        if (attentionItems.length === 0) return null;

        return (
          <Table
            items={attentionItems}
            header={<Header counter={`(${attentionItems.length})`} variant="h2">Needs Attention</Header>}
            columnDefinitions={[
              {
                id: 'accountType',
                header: 'Account Type',
                width: 130,
                cell: (r: AttentionItem) => <Badge>{r.accountType}</Badge>,
                sortingField: 'accountType',
              },
              {
                id: 'sourceField',
                header: 'Source Field',
                cell: (r: AttentionItem) => r.sourceField,
                sortingField: 'sourceField',
              },
              {
                id: 'targetField',
                header: 'Target Field',
                cell: (r: AttentionItem) => r.targetField || '—',
              },
              {
                id: 'confidence',
                header: 'Confidence',
                width: 110,
                cell: (r: AttentionItem) => (
                  <Badge color={r.confidence < 50 ? 'red' : 'blue'}>{r.confidence}%</Badge>
                ),
                sortingField: 'confidence',
              },
              {
                id: 'classification',
                header: 'Classification',
                width: 180,
                cell: (r: AttentionItem) => (
                  <Badge color={r.classification === 'warning' ? 'red' : r.classification === 'requires-human-decision' ? 'red' : 'blue'}>
                    {r.classification.replace(/-/g, ' ')}
                  </Badge>
                ),
              },
            ]}
            sortingDisabled={false}
            empty="No fields need attention"
          />
        );
      })()}
    </SpaceBetween>
  );
}

/* ── Main JobDetail Component ── */

export interface JobDetailProps {
  onJobLoaded?: (props: { jobId: string; processedBy?: string } | null) => void;
  reloadRef?: React.MutableRefObject<(() => void) | null>;
  reassignmentRef?: React.MutableRefObject<((targetAccountType: string) => void) | null>;
  onFieldSelect?: (data: import('../App').FieldPreviewData) => void;
  onAskAgent?: (prompt: string) => void;
}

const JobDetail: React.FC<JobDetailProps> = ({ onJobLoaded, reloadRef, reassignmentRef, onFieldSelect, onAskAgent }) => {
  const { jobId } = useParams<{ jobId: string }>();
  const [detail, setDetail] = useState<JobDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [changedFields, setChangedFields] = useState<Set<string>>(new Set());
  const [flashItems, setFlashItems] = useState<any[]>([]);
  const [duplicateWarningDismissed, setDuplicateWarningDismissed] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const [reassignError, setReassignError] = useState<string | null>(null);
  const [rerunLoading, setRerunLoading] = useState(false);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  // Track account types currently being re-mapped in the background
  // Maps accountType → { startedAt, previousMappingHash } for reliable completion detection
  const [remappingGroups, setRemappingGroups] = useState<Map<string, { startedAt: number; prevHash: string }>>(new Map());
  const remapPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadDetail = useCallback(() => {
    if (!jobId) return;
    setLoading(true);
    fetchJobDetail(jobId)
      .then(data => {
        setDetail(data);
        onJobLoaded?.({ jobId: data.jobId, processedBy: data.processedBy });
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [jobId, onJobLoaded]);

  useEffect(loadDetail, [loadDetail]);

  // On initial load, detect any in-progress remaps from server state and show banners
  useEffect(() => {
    if (!detail?.remapStatuses) return;
    const inProgress = detail.remapStatuses.filter(rs => rs.status === 'in-progress');
    if (inProgress.length === 0) return;
    // Only set if we don't already have client-side tracking for these
    setRemappingGroups(prev => {
      const next = new Map(prev);
      for (const rs of inProgress) {
        if (!next.has(rs.accountType)) {
          next.set(rs.accountType, { startedAt: new Date(rs.requestedAt).getTime(), prevHash: '' });
        }
      }
      return next;
    });
    setFlashItems(prev => {
      const existing = new Set(prev.map(i => i.id));
      const newItems = inProgress
        .filter(rs => !prev.some(i => i.id?.startsWith(`remap-progress-${rs.accountType}`)))
        .map(rs => ({
          type: 'info' as const,
          dismissible: true,
          loading: true,
          content: `Mappings for "${rs.accountType}" are being regenerated by the agent...`,
          id: `remap-progress-${rs.accountType}-${Date.now()}`,
          onDismiss: ({ detail: d }: any) =>
            setFlashItems(p => p.filter(item => item.id !== d.id)),
        }));
      return [...prev, ...newItems];
    });
  }, [detail?.remapStatuses?.length]);

  // Clear chat panel on unmount
  useEffect(() => {
    return () => onJobLoaded?.(null);
  }, [onJobLoaded]);

  // Register reload function so chat panel can trigger a refresh
  useEffect(() => {
    if (reloadRef) {
      reloadRef.current = loadDetail;
    }
    return () => {
      if (reloadRef) reloadRef.current = null;
    };
  }, [reloadRef, loadDetail]);

  // Register reassignment callback so chat panel can trigger remapping indicators
  useEffect(() => {
    if (reassignmentRef) {
      reassignmentRef.current = (targetAccountType: string) => {
        setRemappingGroups(prev => new Map(prev).set(targetAccountType, { startedAt: Date.now(), prevHash: '' }));
        setFlashItems(prev => [
          ...prev,
          {
            type: 'info',
            dismissible: true,
            loading: true,
            content: `Row moved to "${targetAccountType}" via chat. Mappings are being regenerated in the background...`,
            id: `remap-progress-${targetAccountType}-${Date.now()}`,
            onDismiss: ({ detail: d }: any) =>
              setFlashItems(prev => prev.filter(item => item.id !== d.id)),
          },
        ]);
      };
    }
    return () => {
      if (reassignmentRef) reassignmentRef.current = null;
    };
  }, [reassignmentRef]);

  // Derive active remaps from server-side remapStatuses + client-side tracking
  const activeRemaps = React.useMemo(() => {
    const active = new Map<string, { startedAt: number }>();
    // Server-side in-progress remaps
    for (const rs of detail?.remapStatuses ?? []) {
      if (rs.status === 'in-progress') {
        active.set(rs.accountType, { startedAt: new Date(rs.requestedAt).getTime() });
      }
    }
    // Client-side tracking (for immediate feedback before server state updates)
    for (const [acctType, meta] of remappingGroups) {
      if (!active.has(acctType)) {
        // Check if server says it's completed/failed already
        const serverStatus = detail?.remapStatuses?.find(rs => rs.accountType === acctType);
        if (!serverStatus || serverStatus.status === 'in-progress') {
          active.set(acctType, meta);
        }
      }
    }
    return active;
  }, [detail?.remapStatuses, remappingGroups]);

  // Poll for re-mapping completion when there are active remaps
  useEffect(() => {
    if (activeRemaps.size === 0) {
      if (remapPollRef.current) {
        clearInterval(remapPollRef.current);
        remapPollRef.current = null;
      }
      return;
    }
    // Poll every 5 seconds
    remapPollRef.current = setInterval(async () => {
      if (!jobId) return;
      try {
        const freshData = await fetchJobDetail(jobId);
        setDetail(freshData);

        // Check server-side remap statuses for completion
        const completed = new Set<string>();
        const failed = new Set<string>();
        for (const acctType of activeRemaps.keys()) {
          const serverStatus = freshData.remapStatuses?.find(rs => rs.accountType === acctType);
          if (serverStatus?.status === 'completed') {
            completed.add(acctType);
          } else if (serverStatus?.status === 'failed') {
            failed.add(acctType);
          }
        }

        const resolved = new Set([...completed, ...failed]);
        if (resolved.size > 0) {
          // Clear resolved groups from client-side tracking
          setRemappingGroups(prev => {
            const next = new Map(prev);
            resolved.forEach(t => next.delete(t));
            return next;
          });
          // Remove "in progress" flash items
          setFlashItems(prev => prev.filter(item => {
            for (const t of resolved) {
              if (item.id?.startsWith(`remap-progress-${t}`)) return false;
            }
            return true;
          }));
          // Add completion flash items
          for (const t of completed) {
            const meta = activeRemaps.get(t);
            const elapsed = meta ? Math.round((Date.now() - meta.startedAt) / 1000) : 0;
            setFlashItems(prev => [
              ...prev,
              {
                type: 'success',
                dismissible: true,
                content: `Mappings for "${t}" have been updated (${elapsed}s).`,
                id: `remap-done-${t}-${Date.now()}`,
                onDismiss: ({ detail: d }: any) =>
                  setFlashItems(prev => prev.filter(item => item.id !== d.id)),
              },
            ]);
          }
          for (const t of failed) {
            const serverStatus = freshData.remapStatuses?.find(rs => rs.accountType === t);
            setFlashItems(prev => [
              ...prev,
              {
                type: 'error',
                dismissible: true,
                content: `Remapping failed for "${t}": ${serverStatus?.errorMessage ?? 'Unknown error'}`,
                id: `remap-fail-${t}-${Date.now()}`,
                onDismiss: ({ detail: d }: any) =>
                  setFlashItems(prev => prev.filter(item => item.id !== d.id)),
              },
            ]);
          }
        } else {
          // Update elapsed time on in-progress flash items
          setFlashItems(prev => prev.map(item => {
            if (!item.id?.startsWith('remap-progress-')) return item;
            const acctType = item.id.replace('remap-progress-', '').replace(/-\d+$/, '');
            const meta = activeRemaps.get(acctType);
            if (!meta) return item;
            const elapsed = Math.round((Date.now() - meta.startedAt) / 1000);
            return {
              ...item,
              content: `Mappings for "${acctType}" are being regenerated by the agent... (${elapsed}s)`,
            };
          }));
        }
      } catch {
        // Silently ignore poll errors
      }
    }, 5000);
    return () => {
      if (remapPollRef.current) {
        clearInterval(remapPollRef.current);
        remapPollRef.current = null;
      }
    };
  }, [activeRemaps, jobId]);

  const handleTargetFieldChange = useCallback(
    async (sourceField: string, newTarget: string) => {
      if (!jobId) return;
      try {
        await updateMapping(jobId, sourceField, newTarget);
        setChangedFields(prev => new Set(prev).add(sourceField));
        setFlashItems(prev => [
          ...prev,
          {
            type: 'success',
            dismissible: true,
            content: `Updated "${sourceField}" → "${newTarget}"`,
            id: `${sourceField}-${Date.now()}`,
            onDismiss: ({ detail: d }: any) =>
              setFlashItems(prev => prev.filter(item => item.id !== d.id)),
          },
        ]);
        loadDetail();
      } catch (e: any) {
        setFlashItems(prev => [
          ...prev,
          {
            type: 'error',
            dismissible: true,
            content: `Failed to update "${sourceField}": ${e.message}`,
            id: `err-${sourceField}-${Date.now()}`,
            onDismiss: ({ detail: d }: any) =>
              setFlashItems(prev => prev.filter(item => item.id !== d.id)),
          },
        ]);
      }
    },
    [jobId, loadDetail],
  );

  const handleReassign = useCallback(
    async (rowIndex: number, targetAccountType: string) => {
      if (!jobId) return;
      setReassigning(true);
      setReassignError(null);
      try {
        await reassignRows(jobId, [rowIndex], targetAccountType);
        // Grouping updated — reload to reflect new groups immediately
        loadDetail();
        // Track that this group's mappings are being regenerated in the background
        setRemappingGroups(prev => new Map(prev).set(targetAccountType, { startedAt: Date.now(), prevHash: '' }));
        setFlashItems(prev => [
          ...prev,
          {
            type: 'info',
            dismissible: true,
            loading: true,
            content: `Row moved to "${targetAccountType}". Mappings are being regenerated in the background...`,
            id: `remap-progress-${targetAccountType}-${Date.now()}`,
            onDismiss: ({ detail: d }: any) =>
              setFlashItems(prev => prev.filter(item => item.id !== d.id)),
          },
        ]);
      } catch (e: any) {
        const msg = e.message || 'Reassignment failed';
        setReassignError(msg);
        setFlashItems(prev => [
          ...prev,
          {
            type: 'error',
            dismissible: true,
            content: `Reassignment failed: ${msg}`,
            id: `reassign-err-${Date.now()}`,
            onDismiss: ({ detail: d }: any) =>
              setFlashItems(prev => prev.filter(item => item.id !== d.id)),
          },
        ]);
      } finally {
        setReassigning(false);
      }
    },
    [jobId, loadDetail],
  );

  const handleRerun = useCallback(async (mode?: import('../services/api').ClassificationMode) => {
    if (!jobId) return;
    setRerunLoading(true);
    try {
      await rerunMapping(jobId, mode);
      loadDetail();
    } catch (e: any) {
      setFlashItems(prev => [
        ...prev,
        {
          type: 'error',
          dismissible: true,
          content: `Re-run failed: ${e.message}`,
          id: `rerun-err-${Date.now()}`,
          onDismiss: ({ detail: d }: any) =>
            setFlashItems(prev => prev.filter(item => item.id !== d.id)),
        },
      ]);
    } finally {
      setRerunLoading(false);
    }
  }, [jobId, loadDetail]);

  const handleSaveAsTemplate = useCallback(async () => {
    if (!jobId) return;
    setSavingTemplate(true);
    try {
      await saveAsTemplate(jobId);
      setShowSaveTemplateModal(false);
      setFlashItems(prev => [
        ...prev,
        {
          type: 'success',
          dismissible: true,
          content: 'Template saved successfully.',
          id: `save-template-${Date.now()}`,
          onDismiss: ({ detail: d }: any) =>
            setFlashItems(prev => prev.filter(item => item.id !== d.id)),
        },
      ]);
      loadDetail();
    } catch (e: any) {
      setFlashItems(prev => [
        ...prev,
        {
          type: 'error',
          dismissible: true,
          content: `Failed to save template: ${e.message}`,
          id: `save-template-err-${Date.now()}`,
          onDismiss: ({ detail: d }: any) =>
            setFlashItems(prev => prev.filter(item => item.id !== d.id)),
        },
      ]);
      setShowSaveTemplateModal(false);
    } finally {
      setSavingTemplate(false);
    }
  }, [jobId, loadDetail]);

  const hasGroupResults = (detail?.groupResults?.length ?? 0) > 0;
  const defaultGroupAccountType = React.useMemo(() => {
    if (!hasGroupResults || !detail?.groupResults || detail.groupResults.length === 0) return '';
    if (detail.rowGroupings && Object.keys(detail.rowGroupings).length > 0) {
      let maxType = detail.groupResults[0].accountType;
      let maxCount = 0;
      for (const gr of detail.groupResults) {
        const count = detail.rowGroupings[gr.accountType]?.length ?? 0;
        if (count > maxCount) {
          maxCount = count;
          maxType = gr.accountType;
        }
      }
      return maxType;
    }
    return detail.groupResults[0].accountType;
  }, [hasGroupResults, detail?.groupResults, detail?.rowGroupings]);

  const [selectedGroup, setSelectedGroup] = useState<string>('');

  // Sync selectedGroup when detail loads or changes
  useEffect(() => {
    if (defaultGroupAccountType) {
      setSelectedGroup(defaultGroupAccountType);
    }
  }, [defaultGroupAccountType]);

  // Detect duplicate target field assignments using findDuplicateTargets
  const duplicatesByGroup = React.useMemo(() => {
    const map = new Map<string, [string, string[]][]>();
    if (hasGroupResults && detail?.groupResults) {
      for (const gr of detail.groupResults) {
        map.set(gr.accountType, findDuplicateTargets(gr.mappings));
      }
    }
    return map;
  }, [hasGroupResults, detail?.groupResults]);

  if (loading && !detail) return <StatusIndicator type="loading">Loading...</StatusIndicator>;
  if (error || !detail) return <StatusIndicator type="error">{error || 'Job not found'}</StatusIndicator>;

  // attentionFields is computed after activeMappings below

  // Parse CSV into row objects for the record navigator
  const csvLines = detail.inputFilePreview;
  const csvHeaders = csvLines.length > 0 ? csvLines[0].split(',').map(h => h.trim()) : [];
  const csvRows = csvLines.slice(1).map(line => {
    const values = line.split(',');
    const row: Record<string, string> = {};
    csvHeaders.forEach((header, i) => {
      row[header] = values[i]?.trim() || '';
    });
    return row;
  });

  // Get full target field list from schema based on account type, fall back to mapped fields
  const accountType = (detail.summary?.accountType || '').toLowerCase().replace(/[\s-_]/g, '');
  const schemaFields = (targetSchema as Record<string, string[]>)[accountType];
  const mappedFields = Array.from(new Set(detail.mappings.map(m => m.targetField).filter(Boolean)));
  const allTargetFields = schemaFields
    ? Array.from(new Set([...schemaFields, ...mappedFields])).sort()
    : mappedFields.sort();

  const duplicateTargets = hasGroupResults
    ? (duplicatesByGroup.get(selectedGroup) ?? [])
    : findDuplicateTargets(detail.mappings);
  const hasDuplicates = duplicateTargets.length > 0;

  // Derive rowGroupings from groupingSummary if not provided by API (pre-deploy fallback)
  const effectiveRowGroupings: Record<string, number[]> | undefined = (() => {
    if (detail.rowGroupings && Object.keys(detail.rowGroupings).length > 0) return detail.rowGroupings;
    const gs = detail.summary?.groupingSummary as any;
    if (!gs?.groups) return undefined;
    const derived: Record<string, number[]> = {};
    for (const rawGroup of gs.groups) {
      const acctType = rawGroup.accountType ?? rawGroup.account_type ?? '';
      const indices = rawGroup.rowIndices ?? rawGroup.row_indices ?? [];
      if (acctType && indices.length > 0) derived[acctType] = indices;
    }
    return Object.keys(derived).length > 0 ? derived : undefined;
  })();

  const duplicateFlashItems = hasDuplicates && !duplicateWarningDismissed
    ? [{
        type: 'warning' as const,
        content: `Duplicate target mappings detected: ${duplicateTargets.map(([target, sources]) => `"${target}" is mapped by ${sources.join(', ')}`).join('; ')}. Resolve before approving.`,
        id: 'duplicate-warning',
        dismissible: true,
        onDismiss: () => setDuplicateWarningDismissed(true),
      }]
    : [];

  // Resolve the mappings to display: selected group's mappings for multi-group, flat mappings otherwise
  const activeMappings = hasGroupResults
    ? (detail.groupResults!.find(g => g.accountType === selectedGroup)?.mappings ?? detail.mappings)
    : detail.mappings;

  const attentionFields = activeMappings.filter(m => needsAttention(m) && !isEmptyField(m));
  const mappingsTabLabel = attentionFields.length > 0 ? 'Mappings (needs attention)' : 'Mappings';

  return (
    <SpaceBetween size="l">
      <Flashbar items={[...duplicateFlashItems, ...flashItems]} />
      <Header variant="h1" description={`Status: ${detail.metadata.status}`}>
        Job {detail.jobId}
      </Header>
      <TemplateMatchBanner
        templateMatchInfo={detail.templateMatchInfo}
        onRerun={handleRerun}
        rerunLoading={rerunLoading}
      />
      {/* Save as Template button or Template Saved indicator */}
      {(detail.metadata.status === 'completed' || detail.metadata.status === 'approved') && (
        detail.savedAsTemplate === true ? (
          <Container>
            <SpaceBetween direction="horizontal" size="xs">
              <StatusIndicator type="success">Template Saved</StatusIndicator>
              {detail.templateId && (
                <Link href={`/library`}>View in library</Link>
              )}
            </SpaceBetween>
          </Container>
        ) : (
          <Container>
            <Button onClick={() => setShowSaveTemplateModal(true)}>Save as Template</Button>
          </Container>
        )
      )}
      {/* Save as Template confirmation modal */}
      <Modal
        visible={showSaveTemplateModal}
        onDismiss={() => setShowSaveTemplateModal(false)}
        header="Save as Template"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowSaveTemplateModal(false)}>Cancel</Button>
              <Button variant="primary" loading={savingTemplate} onClick={handleSaveAsTemplate}>Confirm</Button>
            </SpaceBetween>
          </Box>
        }
      >
        Are you sure you want to save this job's mappings as a template in the Schema Library?
      </Modal>
      <Tabs
        tabs={[
          {
            id: 'summary',
            label: 'Agent Summary',
            content: (
              <SummaryTab
                summary={detail.summary}
                mappings={activeMappings}
                groupingSummary={detail.summary.groupingSummary}
                groupResults={detail.groupResults}
                groupValidationResults={
                  hasGroupResults && selectedGroup
                    ? detail.groupResults?.find(g => g.accountType === selectedGroup)?.validationResults
                    : undefined
                }
              />
            ),
          },
          {
            id: 'mappings',
            label: mappingsTabLabel,
            content: (
              <SpaceBetween size="m">
                {hasGroupResults && detail.groupResults && detail.groupResults.length > 0 && (
                  <AccountTypeSelector
                    groupResults={detail.groupResults}
                    selectedAccountType={selectedGroup}
                    onSelect={(accountType: string) => { setSelectedGroup(accountType); setDuplicateWarningDismissed(false); }}
                    remapStatuses={detail.remapStatuses}
                  />
                )}
                {activeRemaps.has(selectedGroup) && (
                  <Box variant="div" padding={{ vertical: 's', horizontal: 'm' }}>
                    <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                      <Spinner size="normal" />
                      <Box color="text-status-info">Mappings for "{selectedGroup}" are being regenerated by the agent. This page will update automatically when complete.</Box>
                    </SpaceBetween>
                  </Box>
                )}
                <MappingsTab
                  mappings={activeMappings}
                  csvRows={csvRows}
                  outputRows={detail.outputFilePreview}
                  allTargetFields={allTargetFields}
                  onTargetFieldChange={handleTargetFieldChange}
                  onAskAgent={onAskAgent || (() => {})}
                  changedFields={changedFields}
                  onFieldSelect={onFieldSelect}
                  rowGroupings={effectiveRowGroupings}
                  jobId={jobId}
                  reassigning={reassigning}
                  onReassign={handleReassign}
                  groupResults={detail.groupResults}
                  onRecordGroupChange={(acctType) => setSelectedGroup(acctType)}
                  validationWarnings={
                    hasGroupResults && selectedGroup
                      ? detail.groupResults?.find(g => g.accountType === selectedGroup)?.validationResults?.warnings
                      : detail.validationResults?.warnings
                  }
                />
              </SpaceBetween>
            ),
          },
          {
            id: 'input',
            label: `Input Preview (${csvRows.length} rows)`,
            content: <InputPreviewTab csvLines={detail.inputFilePreview} rowGroupings={effectiveRowGroupings} />,
          },
          {
            id: 'output',
            label: 'Output Preview',
            content: (
              <OutputPreviewTab
                outputFilePreview={detail.outputFilePreview}
                jobId={detail.jobId}
                rowGroupings={effectiveRowGroupings}
                hasChangedMappings={changedFields.size > 0}
                onOutputRefreshed={(newPreview) => {
                  setDetail(prev => prev ? { ...prev, outputFilePreview: newPreview } : prev);
                  setChangedFields(new Set());
                }}
              />
            ),
          },
          {
            id: 'approval',
            label: 'Approval',
            content: hasGroupResults && detail.groupResults && detail.groupResults.length > 0 ? (
              <GroupedApprovalWorkflow
                jobId={detail.jobId}
                groupResults={detail.groupResults}
                onAction={loadDetail}
                duplicatesByGroup={duplicatesByGroup}
              />
            ) : (
              <ApprovalWorkflow
                jobId={detail.jobId}
                mappings={detail.mappings}
                onAction={loadDetail}
                hasDuplicates={hasDuplicates}
              />
            ),
          },
        ]}
      />
    </SpaceBetween>
  );
};

export default JobDetail;
